import {
  Activity, BatteryLow, Brain, CalendarClock, CloudOff, CloudRain, Feather,
  Focus, Frown, Hand, HeartOff, HeartPulse, ListChecks, Moon, RotateCcw,
  ShieldCheck, Sparkles, Sun, Sunrise, Wind, Zap,
} from "lucide-react";

export const GRADIENTS = {
  effects: "linear-gradient(to right, #E9E2D8, #E5E7EB, #374151, #142D43)",
  benefits: "linear-gradient(to right, #E9E2D8, #E5E7EB, #374151, #142D43)",
  neutral: "linear-gradient(to right, #E5E7EB, #F4F6F8, #E5E7EB)",
  appetite: "linear-gradient(to right, #E5E7EB, #F4F6F8, #E5E7EB)",
};

export const NPA_CONFIG = {
  path: "npa",
  effects: [
    "Irritabilità / nervosismo",
    "Ansia / agitazione",
    "Mal di testa",
    "Palpitazioni / battito accelerato",
    "Effetto rebound",
    "Wearing-off",
    "Crash",
  ],
  benefits: [
    { label: "Attenzione e concentrazione", question: "Quanto è migliorata questa area?" },
    { label: "Iniziare e completare le attività", question: "Quanto è migliorata questa area?" },
    { label: "Organizzazione e gestione del tempo", question: "Quanto è migliorata questa area?" },
    { label: "Controllo dell'impulsività", question: "Quanto è migliorata questa area?" },
    { label: "Calma e minore irrequietezza", question: "Quanto è migliorata questa area?" },
  ],
};

export const NPX_CONFIG = {
  path: "npx",
  effects: [
    "Nausea / disturbi gastrointestinali",
    "Stanchezza / sonnolenza",
    "Agitazione / irrequietezza",
    "Mal di testa",
    "Disturbi della sfera sessuale",
  ],
  benefits: [
    { label: "Riduzione dell'ansia", question: "Quanto è diminuita l'ansia?" },
    { label: "Miglioramento dell'umore", question: "Quanto è migliorato il tuo umore?" },
    {
      label: "Riduzione di pensieri negativi e rimuginazione",
      question: "Quanto sono diminuiti i pensieri negativi, le preoccupazioni ripetitive o il rimuginare?",
    },
    {
      label: "Maggiore energia e motivazione",
      question: "Quanto sono migliorate la tua energia e la tua motivazione?",
    },
    {
      label: "Miglioramento del funzionamento quotidiano",
      question: "Quanto senti di funzionare meglio nella vita quotidiana?",
      sub: "Considera le attività personali, familiari, sociali e lavorative.",
    },
  ],
};

export const OPTION_ICONS = {
  "Irritabilità / nervosismo": Zap,
  "Ansia / agitazione": Wind,
  "Mal di testa": Brain,
  "Palpitazioni / battito accelerato": HeartPulse,
  "Effetto rebound": RotateCcw,
  "Wearing-off": BatteryLow,
  "Crash": CloudRain,
  "Nausea / disturbi gastrointestinali": Frown,
  "Stanchezza / sonnolenza": Moon,
  "Agitazione / irrequietezza": Activity,
  "Disturbi della sfera sessuale": HeartOff,
  "Attenzione e concentrazione": Focus,
  "Iniziare e completare le attività": ListChecks,
  "Organizzazione e gestione del tempo": CalendarClock,
  "Controllo dell'impulsività": Hand,
  "Calma e minore irrequietezza": Feather,
  "Riduzione dell'ansia": ShieldCheck,
  "Miglioramento dell'umore": Sun,
  "Riduzione di pensieri negativi e rimuginazione": CloudOff,
  "Maggiore energia e motivazione": Sunrise,
  "Miglioramento del funzionamento quotidiano": Sparkles,
};

export const OPTION_DESCRIPTIONS = {
  "Irritabilità / nervosismo": "Più irritabile, teso o nervoso",
  "Ansia / agitazione": "Più ansia o agitazione interna",
  "Mal di testa": "Comparsa o aumento della cefalea",
  "Palpitazioni / battito accelerato": "Cuore più veloce o forte",
  "Effetto rebound": "Sintomi ADHD tornano più intensi",
  "Wearing-off": "L'effetto svanisce troppo presto",
  "Crash": "Calo brusco di energia e umore",
  "Attenzione e concentrazione": "Più facile restare concentrati",
  "Iniziare e completare le attività": "Meno fatica a portarle avanti",
  "Organizzazione e gestione del tempo": "Più facile organizzarsi e pianificare",
  "Controllo dell'impulsività": "Più facile fermarsi prima",
  "Calma e minore irrequietezza": "Più calma, meno bisogno di muoversi",
};

export const DISCLAIMERS = {
  feedback: {
    title: "Prima di inviare il feedback",
    paragraphs: [
      "Il feedback sulla farmacoterapia viene trasmesso in forma anonima: insieme alle risposte non vengono inviati dati anagrafici personali.",
      "Il feedback sarà letto e valutato personalmente dal medico. La lettura e la valutazione costituiscono una prestazione medica e verranno fatturate come una consultazione telemedica differita, corrispondente a 5 minuti di lavoro.",
      "Secondo le disposizioni tariffarie TARDOC attualmente vigenti, questa prestazione può essere fatturata al massimo quattro volte nell'arco di 30 giorni per ciascun paziente. Eventuali ulteriori comunicazioni o feedback inviati oltre tale limite non comportano una nuova fatturazione della medesima prestazione.",
      "L'invio del feedback non sostituisce una consultazione medica e non deve essere utilizzato per comunicazioni urgenti.",
    ],
    checkbox: "Ho letto e compreso le informazioni sopra riportate e desidero inviare il feedback.",
  },
  prescription: {
    title: "Prima di inviare la richiesta",
    paragraphs: [
      "La richiesta di una nuova ricetta viene trasmessa al medico per la verifica e l'elaborazione.",
      "L'invio e la gestione della richiesta di ricetta tramite questo servizio sono gratuiti e non comportano alcuna fatturazione al paziente.",
      "L'invio della richiesta non garantisce automaticamente il rilascio della prescrizione: il medico ne valuterà l'appropriatezza sulla base della terapia in corso e della situazione clinica.",
      "Il servizio non deve essere utilizzato per comunicazioni urgenti.",
    ],
    checkbox: "Ho letto e compreso le informazioni sopra riportate e desidero inviare la richiesta di ricetta.",
  },
};

export const CONTACT_DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì"];
export const CONTACT_SLOTS = ["08:00–11:00", "12:00–13:00", "14:00–17:00", "17:00–20:00"];
